<app-public-shell-layout [showWorkspaceEntry]="isSignedIn()" [showAssistant]="true" [showNav]="true" [fullBleed]="true">
  <!-- Hero + demo request -->
  <section class="hero" id="top">
    <div class="hero__inner">
      <div class="hero__copy">
        <span class="hero__badge">
          <i class="pi pi-verified" aria-hidden="true"></i>
          {{ 'LANDING.HERO.BADGE' | translate }}
        </span>
        <h1>{{ 'LANDING.HERO.TITLE' | translate }}</h1>
        <p class="hero__lede">{{ 'LANDING.HERO.LEDE' | translate }}</p>
        <div class="hero__actions">
          <a routerLink="/" fragment="demo" class="hero__cta hero__cta--light">
            {{ 'LANDING.HERO.CTA_PRIMARY' | translate }}
            <i class="pi pi-arrow-right" aria-hidden="true"></i>
          </a>
          <a routerLink="/" fragment="modules" class="hero__cta hero__cta--outline">{{ 'LANDING.HERO.CTA_SECONDARY' | translate }}</a>
        </div>
        <ul class="hero__checks">
          @for (check of heroChecks; track check) {
            <li><i class="pi pi-check" aria-hidden="true"></i>{{ check | translate }}</li>
          }
        </ul>
        <dl class="hero__stats">
          @for (stat of heroStats; track stat.value) {
            <div>
              <dt>{{ stat.value | translate }}</dt>
              <dd>{{ stat.label | translate }}</dd>
            </div>
          }
        </dl>
      </div>

      <div class="demo" id="demo">
        <p class="demo__eyebrow">{{ 'LANDING.DEMO.EYEBROW' | translate }}</p>
        <h2 class="demo__title">{{ 'LANDING.DEMO.TITLE' | translate }}</h2>
        <p class="demo__lede">{{ 'LANDING.DEMO.LEDE' | translate }}</p>

        @if (demoSubmitted()) {
          <div class="demo__thanks" role="status">
            <i class="pi pi-check-circle" aria-hidden="true"></i>
            <span>{{ 'LANDING.DEMO.THANKS' | translate }}</span>
          </div>
        } @else {
          <form class="demo__form" [formGroup]="demoForm" (ngSubmit)="submitDemo()" novalidate>
            <label class="field">
              <span class="field__label">{{ 'LANDING.DEMO.NAME' | translate }}</span>
              <input class="field__input" type="text" formControlName="name" autocomplete="name" />
              <app-validation-message [control]="demoForm.controls.name" />
            </label>
            <div class="demo__row">
              <label class="field">
                <span class="field__label">{{ 'LANDING.DEMO.EMAIL' | translate }}</span>
                <input class="field__input" type="email" formControlName="email" autocomplete="email" />
                <app-validation-message [control]="demoForm.controls.email" />
              </label>
              <label class="field">
                <span class="field__label">{{ 'LANDING.DEMO.PHONE' | translate }}</span>
                <input class="field__input" type="tel" formControlName="phone" autocomplete="tel" dir="ltr" />
                <app-validation-message [control]="demoForm.controls.phone" />
              </label>
            </div>
            <div class="demo__row">
              <label class="field">
                <span class="field__label">{{ 'LANDING.DEMO.COMPANY' | translate }}</span>
                <input class="field__input" type="text" formControlName="company" autocomplete="organization" />
                <app-validation-message [control]="demoForm.controls.company" />
              </label>
              <label class="field">
                <span class="field__label">{{ 'LANDING.DEMO.EMPLOYEES' | translate }}</span>
                <select class="field__input" formControlName="employees">
                  @for (band of employeeBands; track band) {
                    <option [value]="band">{{ band }}</option>
                  }
                </select>
              </label>
            </div>
            <div class="demo__pains" role="group" [attr.aria-label]="'LANDING.DEMO.PAIN_LABEL' | translate">
              <span class="field__label">{{ 'LANDING.DEMO.PAIN_LABEL' | translate }}</span>
              <div class="demo__chips">
                @for (pain of painOptions; track pain) {
                  <button
                    type="button"
                    class="demo__chip"
                    [class.is-on]="isPainSelected(pain)"
                    [attr.aria-pressed]="isPainSelected(pain)"
                    (click)="togglePain(pain)"
                  >
                    {{ 'LANDING.DEMO.PAIN_' + pain | translate }}
                  </button>
                }
              </div>
            </div>
            <input class="demo__honeypot" type="text" formControlName="honeypot" tabindex="-1" autocomplete="off" aria-hidden="true" />
            @if (demoError(); as err) {
              <p class="demo__error" role="alert">{{ err }}</p>
            }
            <button type="submit" class="demo__submit" [disabled]="demoSubmitting()">{{ 'LANDING.DEMO.SUBMIT' | translate }}</button>
            <p class="demo__footnote">
              {{ 'LANDING.DEMO.FOOTNOTE' | translate }}
              @if (contact(); as c) {
                {{ 'LANDING.DEMO.PREFER_TO_TALK' | translate }}
                <a [href]="phoneHref()" dir="ltr" translate="no">{{ c.phoneNumber }}</a>
              }
            </p>
          </form>
        }
      </div>
    </div>
  </section>

  <!-- Sectors strip -->
  <section class="sectors" [attr.aria-label]="'LANDING.SECTORS.EYEBROW' | translate">
    <div class="sectors__inner">
      <span class="sectors__eyebrow">{{ 'LANDING.SECTORS.EYEBROW' | translate }}</span>
      <ul class="sectors__list">
        @for (sector of sectors; track sector.key) {
          <li><i [class]="sector.icon" aria-hidden="true"></i>{{ sector.key | translate }}</li>
        }
      </ul>
    </div>
  </section>

  <!-- Outcomes -->
  <section class="block" id="outcomes">
    <p class="block__eyebrow">{{ 'LANDING.OUTCOMES.EYEBROW' | translate }}</p>
    <h2 class="block__title">{{ 'LANDING.OUTCOMES.TITLE' | translate }}</h2>
    <p class="block__lede">{{ 'LANDING.OUTCOMES.LEDE' | translate }}</p>
    <div class="cards cards--3">
      @for (item of outcomes; track item.key) {
        <article class="outcome">
          <span class="outcome__icon" aria-hidden="true"><i [class]="item.icon"></i></span>
          <span class="outcome__before">{{ 'LANDING.OUTCOMES.' + item.key + '_BEFORE' | translate }}</span>
          <h3>{{ 'LANDING.OUTCOMES.' + item.key + '_TITLE' | translate }}</h3>
          <p>{{ 'LANDING.OUTCOMES.' + item.key + '_BODY' | translate }}</p>
          <span class="outcome__proof">{{ 'LANDING.OUTCOMES.' + item.key + '_PROOF' | translate }}</span>
        </article>
      }
    </div>
  </section>

  <!-- Modules -->
  <section class="block" id="modules" [attr.aria-label]="'LANDING.MODULES.EYEBROW' | translate">
    <p class="block__eyebrow">{{ 'LANDING.MODULES.EYEBROW' | translate }}</p>
    <h2 class="block__title">{{ 'LANDING.MODULES.TITLE' | translate }}</h2>
    <p class="block__lede">{{ 'LANDING.MODULES.LEDE' | translate }}</p>
    <div class="cards cards--modules">
      @for (module of modules; track module.titleKey) {
        <article class="module">
          <span class="module__icon" aria-hidden="true"><i [class]="module.icon"></i></span>
          <span class="module__copy">
            <h3>{{ module.titleKey | translate }}</h3>
            <p>{{ module.detailKey | translate }}</p>
          </span>
        </article>
      }
    </div>
  </section>

  <!-- Compliance + attendance preview -->
  <section class="block">
    <div class="compliance">
      <div class="compliance__copy">
        <p class="block__eyebrow">{{ 'LANDING.COMPLIANCE.EYEBROW' | translate }}</p>
        <h2 class="compliance__title">{{ 'LANDING.COMPLIANCE.TITLE' | translate }}</h2>
        <p class="compliance__lede">{{ 'LANDING.COMPLIANCE.LEDE' | translate }}</p>
        <ul class="compliance__points">
          @for (point of compliancePoints; track point) {
            <li><i class="pi pi-check-circle" aria-hidden="true"></i>{{ point | translate }}</li>
          }
        </ul>
      </div>
      <div class="compliance__preview" aria-hidden="true">
        <p class="compliance__preview-eyebrow">{{ 'LANDING.FEED.EYEBROW' | translate }}</p>
        <div class="feed">
          <div class="feed__bar">
            <span></span><span></span><span></span>
            <strong>{{ 'LANDING.FEED.TITLE' | translate }}</strong>
          </div>
          @for (row of feed; track row.nameKey) {
            <div class="feed__row">
              <span class="wn-avatar">{{ row.initials }}</span>
              <span class="feed__who">
                <strong>{{ row.nameKey | translate }}</strong>
                <span>{{ row.detailKey | translate }}</span>
              </span>
              <span class="wn-chip" [class]="'wn-chip wn-chip--' + row.tone">{{ row.statusKey | translate }}</span>
            </div>
          }
          <div class="feed__foot">{{ 'LANDING.FEED.FOOT' | translate }}</div>
        </div>
        <p class="compliance__preview-note">{{ 'LANDING.FEED.NOTE' | translate }}</p>
      </div>
    </div>
  </section>

  <!-- How it works -->
  <section class="block" id="how-it-works">
    <p class="block__eyebrow">{{ 'LANDING.HOW_IT_WORKS.EYEBROW' | translate }}</p>
    <h2 class="block__title block__title--tight">{{ 'LANDING.HOW_IT_WORKS.TITLE' | translate }}</h2>
    <ol class="cards cards--3 steps">
      @for (step of steps; track step.n) {
        <li class="step">
          <span class="step__index" aria-hidden="true">{{ step.n }}</span>
          <h3>{{ step.titleKey | translate }}</h3>
          <p>{{ step.detailKey | translate }}</p>
          <span class="step__when">{{ step.whenKey | translate }}</span>
        </li>
      }
    </ol>
  </section>

  <!-- Testimonial (hidden until a real quote replaces the sample copy) -->
  @if (showTestimonial) {
    <section class="block">
      <div class="quote">
        <div class="quote__copy">
          <p class="quote__text">{{ 'LANDING.TESTIMONIAL.QUOTE' | translate }}</p>
          <div class="quote__author">
            <span class="wn-avatar wn-avatar--lg wn-avatar--tint">{{ 'LANDING.TESTIMONIAL.INITIALS' | translate }}</span>
            <span>
              <strong>{{ 'LANDING.TESTIMONIAL.NAME' | translate }}</strong>
              <span>{{ 'LANDING.TESTIMONIAL.ROLE' | translate }}</span>
            </span>
          </div>
        </div>
        <dl class="quote__stats">
          @for (stat of resultStats; track stat.value) {
            <div>
              <dt>{{ stat.value | translate }}</dt>
              <dd>{{ stat.label | translate }}</dd>
            </div>
          }
        </dl>
      </div>
    </section>
  }

  <!-- FAQ -->
  <section class="block" id="faq">
    <h2 class="block__title block__title--faq">{{ 'LANDING.FAQ.TITLE' | translate }}</h2>
    <div class="cards cards--faq">
      @for (faq of faqs; track faq.q) {
        <article class="faq">
          <h3>{{ faq.q | translate }}</h3>
          <p>{{ faq.a | translate }}</p>
        </article>
      }
    </div>
  </section>

  <!-- Closing CTA -->
  <section class="block block--last">
    <div class="banner">
      <div class="banner__copy">
        <h2>{{ 'LANDING.CTA_BANNER.TITLE' | translate }}</h2>
        <p>{{ 'LANDING.CTA_BANNER.LEDE' | translate }}</p>
      </div>
      <div class="banner__actions">
        <div class="banner__row">
          @if (isSignedIn()) {
            <a routerLink="/dashboard" class="hero__cta hero__cta--light">
              {{ 'COMMON.ACTIONS.OPEN_WORKSPACE' | translate }}
              <i class="pi pi-arrow-right" aria-hidden="true"></i>
            </a>
          } @else {
            <a routerLink="/" fragment="demo" class="hero__cta hero__cta--light">
              {{ 'LANDING.CTA_BANNER.CTA' | translate }}
              <i class="pi pi-arrow-right" aria-hidden="true"></i>
            </a>
            <a routerLink="/login" class="hero__cta hero__cta--outline">{{ 'COMMON.ACTIONS.SIGN_IN' | translate }}</a>
          }
        </div>
        @if (contact(); as c) {
          <span class="banner__call">
            {{ 'LANDING.CTA_BANNER.OR_CALL' | translate }}
            <a [href]="phoneHref()" dir="ltr" translate="no">{{ c.phoneNumber }}</a>
          </span>
        }
      </div>
    </div>
  </section>
</app-public-shell-layout>
